import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Target, Send, Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';

// API URL from env
const API = process.env.REACT_APP_BACKEND_URL || '';

const PublicSubmit = () => {
  const { token } = useParams();
  const [input, setInput] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [scrapedTitle, setScrapedTitle] = useState('');
  const [scrapedUrl, setScrapedUrl] = useState('');
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Anti-bot: timestamp d'ouverture de la page
  const [pageLoadedAt] = useState(() => Date.now() / 1000);
  // Anti-bot: honeypot field (doit rester vide)
  const [honeypot, setHoneypot] = useState('');

  // Détecter si c'est une URL et scraper
  useEffect(() => {
    const isUrl = input.startsWith('http://') || input.startsWith('https://');
    
    if (isUrl && input.length > 15) {
      setScraping(true);
      setScrapedTitle('');
      
      const timeoutId = setTimeout(async () => {
        try {
          const res = await axios.post(`${API}/api/public/targets/scrape?token=${token}&url=${encodeURIComponent(input)}`);
          if (res.data.success && res.data.title) {
            setScrapedTitle(res.data.title);
            setScrapedUrl(input);
          }
        } catch (e) {
          // Silencieux
        } finally {
          setScraping(false);
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setScrapedTitle('');
      setScrapedUrl('');
    }
  }, [input, token]);

  const handleSubmit = async () => {
    if (!input.trim() && !scrapedTitle) {
      setError('Veuillez saisir un sujet ou une URL');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const payload = {
        title: scrapedTitle || input.trim(),
        url: scrapedUrl || (input.startsWith('http') ? input.trim() : null),
        priority,
        // Anti-bot fields
        website: honeypot,  // Honeypot - should be empty
        page_loaded_at: pageLoadedAt,  // Timestamp for timing check
      };
      
      const res = await axios.post(`${API}/api/public/targets/submit?token=${token}`, payload);
      
      if (res.data.success) {
        setSuccess(res.data.target.title);
        
        // Reset form
        setInput('');
        setScrapedTitle('');
        setScrapedUrl('');
        setPriority('normal');
        
        // Clear success après 3s
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent', color: 'bg-red-500', emoji: '🔴' },
    { value: 'normal', label: 'Normal', color: 'bg-yellow-500', emoji: '🟡' },
    { value: 'low', label: 'Basse', color: 'bg-green-500', emoji: '🟢' },
  ];

  // Vérifier si le token est présent
  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center text-red-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <p>Lien invalide</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Target className="w-6 h-6 text-amber-400" />
          <h1 className="text-lg font-semibold tracking-wide">Soumettre une cible</h1>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-lg mx-auto p-4 space-y-6">
        
        {/* Success message */}
        {success && (
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 flex items-start gap-3 animate-pulse">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-400 font-medium text-sm">Cible ajoutée !</p>
              <p className="text-green-300/80 text-xs mt-1 truncate">{success}</p>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Input field */}
        <div className="space-y-2">
          <label className="text-slate-400 text-sm">Sujet ou URL</label>
          <div className="relative">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Collez un lien ou tapez un sujet..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 resize-none"
              rows={3}
              autoFocus
            />
            {scraping && (
              <div className="absolute right-3 top-3">
                <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
              </div>
            )}
          </div>
          
          {/* Honeypot field - invisible pour les humains, rempli par les bots */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              opacity: 0,
              height: 0,
              width: 0,
              pointerEvents: 'none',
            }}
          />
          
          {/* Scraped title preview */}
          {scrapedTitle && (
            <div className="bg-slate-800/50 border border-cyan-500/30 rounded-lg p-3">
              <p className="text-[11px] text-cyan-400 uppercase tracking-wider mb-1">Titre détecté</p>
              <p className="text-sm text-white">{scrapedTitle}</p>
              {scrapedUrl && (
                <a 
                  href={scrapedUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[11px] text-slate-500 hover:text-slate-400 flex items-center gap-1 mt-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  {scrapedUrl.length > 40 ? scrapedUrl.slice(0, 40) + '...' : scrapedUrl}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Priority selection */}
        <div className="space-y-2">
          <label className="text-slate-400 text-sm">Priorité</label>
          <div className="grid grid-cols-3 gap-2">
            {priorityOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPriority(opt.value)}
                className={`
                  py-3 px-4 rounded-lg text-sm font-medium transition-all
                  ${priority === opt.value 
                    ? 'bg-amber-400 text-slate-900 shadow-lg ring-2 ring-amber-300' 
                    : 'bg-sky-100 text-slate-700 hover:bg-sky-200'}
                `}
              >
                <span className="mr-1">{opt.emoji}</span>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={submitting || (!input.trim() && !scrapedTitle)}
          className={`
            w-full py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 transition-all
            ${submitting || (!input.trim() && !scrapedTitle)
              ? 'bg-emerald-200 text-emerald-600 cursor-not-allowed'
              : 'bg-emerald-400 hover:bg-emerald-500 text-slate-900 active:scale-[0.98]'}
          `}
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Envoi...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Envoyer
            </>
          )}
        </button>

        {/* Footer */}
        <div className="text-center pt-6">
          <p className="text-slate-600 text-xs">Auto Edito</p>
        </div>
      </div>
    </div>
  );
};

export default PublicSubmit;
